import { Modal } from '../../../../customComponents/Modal'
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { CommentField } from '../../../../customComponents/comments/CommentField'
import { CommentParentType } from '../../../../../models/Comment'
import { type PlanOrganizationTaskStatus } from '../../../../../models/PlanOrganizationTask'
import { type PriorityActivity } from '../../../../../models/PriorityActivity'
import { useEffect, useRef, useState } from 'react'

interface Props {
  statusForLockModal: PlanOrganizationTaskStatus | null
  handleConfirmLockModal: () => void
  handleCloseLockModal: () => void
  statusForSomeItemsLockModal: PlanOrganizationTaskStatus | null
  handleConfirmNoItemsLockModal: () => void
  handleCloseNoItemsLockModal: () => void
  statusForSubmittedToInProgressModal: PlanOrganizationTaskStatus | null
  handleConfirmSubmittedToInProgressModal: () => void
  handleCloseSubmittedToInProgressModal: () => void
  statusForSubmittedToAcceptedModal: PlanOrganizationTaskStatus | null
  handleConfirmSubmittedToAcceptedModal: () => void
  handleCloseSubmittedToAcceptedModal: () => void
  statusForSubmittedToNeedsRevisionModal: PlanOrganizationTaskStatus | null
  handleConfirmSubmittedToNeedsRevision: (comment: string, alreadyLeftFeedback: boolean) => void
  handleCloseSubmittedToNeedsRevision: () => void
  activities: PriorityActivity[]
  planOrganizationTaskId: number
  priorityNumber: number
}

export const OverviewStatusModals = (props: Props): JSX.Element => {
  const [leaveFeedbackContent, setLeaveFeedbackContent] = useState<string>('')
  const [alreadyProvidedFeedback, setAlreadyProvidedFeedback] = useState(false)
  const contentEditableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // reset feedback content and already provided feedback state when modal is closed
    setLeaveFeedbackContent('')
    setAlreadyProvidedFeedback(false)
  }, [props.statusForSubmittedToNeedsRevisionModal])

  const notStartedActivities = props.activities.filter((activity) => !activity.started)

  return <>
    <Modal
      open={props.statusForLockModal !== null}
      title='Are you sure?'
      cancelButtonText='No, I am not ready'
      confirmButtonText='Yes, Submit'
      data-testid='confirmLockPriorityFourActivitiesModal'
      bodyContent={`This will lock the activities for Priority ${props.priorityNumber}, are you sure you wish to continue?`}
      onClose={props.handleCloseLockModal}
      onConfirm={props.handleConfirmLockModal}
      maxWidth='xs'
    />

    <Modal
      open={props.statusForSomeItemsLockModal !== null}
      title='Are you sure?'
      cancelButtonText='No, I am not ready'
      confirmButtonText='Yes, Submit'
      data-testid='confirmLockPriorityFourWithSomeActivitiesModal'
      bodyContent={`You currently have no activities logged for ${notStartedActivities.map(a => a.name).join(', ')}. Changing the status to “Submitted” will lock the activities for Priority ${props.priorityNumber}. Are you sure you wish to continue?`}
      onClose={props.handleCloseNoItemsLockModal}
      onConfirm={props.handleConfirmNoItemsLockModal}
      maxWidth='xs'
    />

    <Modal
      open={props.statusForSubmittedToInProgressModal !== null}
      title='Are you sure?'
      cancelButtonText='No, Cancel'
      confirmButtonText='Yes, Continue'
      data-testid='confirmSubmittedToInProgressModal'
      bodyContent={`This will unlock the activities for Priority ${props.priorityNumber} and mark the task "Incomplete", do you wish to continue?`}
      onClose={props.handleCloseSubmittedToInProgressModal}
      onConfirm={props.handleConfirmSubmittedToInProgressModal}
      maxWidth='xs'
    />

    <Modal
      open={props.statusForSubmittedToAcceptedModal !== null}
      title='Are you sure?'
      cancelButtonText='No, Cancel'
      confirmButtonText='Yes, Continue'
      data-testid='confirmSubmittedToAcceptedModal'
      bodyContent={`This will lock the activities for Priority ${props.priorityNumber} and mark the task as "Accepted." Do you wish to continue?`}
      onClose={props.handleCloseSubmittedToAcceptedModal}
      onConfirm={props.handleConfirmSubmittedToAcceptedModal}
      maxWidth='xs'
    />

    {props.statusForSubmittedToNeedsRevisionModal &&
      <Modal
        data-testid='confirmSubmittedToNeedsRevisionModal'
        title='Are you sure?'
        bodyContent={<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography variant='body1'>This will unlock the activities for Priority {props.priorityNumber} and mark the task as “Needs Revision.” Please leave feedback so that the contributors can resolve the issue.</Typography>
          <CommentField
            parentId={props.planOrganizationTaskId}
            parentType={CommentParentType.PlanOrganizationTask}
            contentEditableRef={contentEditableRef}
            setContent={setLeaveFeedbackContent}
          />
          <FormControlLabel
            data-testid='alreadyProvidedFeedbackCheckbox'
            label="I have already provided feedback."
            control={<Checkbox checked={alreadyProvidedFeedback} onChange={(e) => {
              setAlreadyProvidedFeedback(e.target.checked)
            }}
            />}
          />
        </Box>}
        open={true}
        onConfirm={() => {
          props.handleConfirmSubmittedToNeedsRevision(leaveFeedbackContent, alreadyProvidedFeedback)
        }}
        onClose={props.handleCloseSubmittedToNeedsRevision}
        cancelButtonText='No, Cancel'
        confirmButtonText='Yes, Continue'
      />
    }
  </>
}
